<template>
  <div class="conversation-products">
    <article v-for="product in products" :key="product.product_id">
      <CustomImg :src="product.main_image" :alt="product.product_name" />
      <div class="conversation-products__content">
        <strong>{{ product.product_name }}</strong>
        <small v-if="product.category">{{ product.category }}</small>
        <small v-if="product.reason">{{ product.reason }}</small>

        <div class="conversation-products__actions">
          <PillButton
            v-if="getProductUrl(product, 'detail')"
            :content="t('ai.result.product.detail')"
            :link="getProductUrl(product, 'detail')"
            target="_blank"
            rel="noopener noreferrer"
          />
          <PillButton
            v-if="getProductUrl(product, 'specify')"
            :content="t('ai.result.product.specify')"
            :link="getProductUrl(product, 'specify')"
            target="_blank"
            rel="noopener noreferrer"
          />
        </div>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import CustomImg from '@/components/CustomImg.vue';
import PillButton from '@/components/button.vue';
import { runtimeConfig } from '@/config/runtime';
import { getChatSiteByLanguage } from '@/tools/chat_api';
import type { ChatProduct } from '@/tools/chat_api';
import type { Translate } from './types';

defineProps<{
  products: ChatProduct[];
  t: Translate;
}>();

// 商品链接必须直接使用接口字段，不能根据商品 ID 猜测或拼接地址。
type ProductLinkType = 'detail' | 'specify';

const getProductUrl = (
  product: ChatProduct,
  type: ProductLinkType,
): string | undefined => {
  const site = getChatSiteByLanguage(runtimeConfig.lang);
  const suffix = type === 'detail' ? 'url' : 'stage_url';
  const key = `${site}_${suffix}` as keyof ChatProduct;
  const url = product[key];

  return typeof url === 'string' && url.length > 0 ? url : undefined;
};
</script>

<style scoped lang="scss">
.conversation-products {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 22px;

  article {
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    color: inherit;
    background: #fff;
    box-shadow: 0 5px 20px rgba(0, 0, 0, .08);
    overflow: hidden;
    padding: 10px 0;
  }

  img {
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: contain;
    transition: transform .3s ease;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 5px;
    padding: 12px 14px 14px;
  }

  &__actions {
    display: flex;
    gap: 7px;
    justify-content: center;
    margin-top: auto;
    padding-top: 14px;

    :deep(.pill-button) {
      min-width: 0;
      width: 100%;
    }
  }

  small { color: #777; line-height: 1.45; }
  article:hover img { transform: scale(1.04); }
}

@media (max-width: 600px) {
  .conversation-products {
    grid-template-columns: 1fr;

    img {
      align-self: center;
      width: 72%;
    }
  }
}
</style>
